import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrashIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";
import { useAuthStore } from "../../stores/auth";
import {
  AdminTable,
  type AdminTableColumn,
  ConfirmModal,
  Pagination,
  PageHeader,
  SearchBar,
  useDebouncedValue,
} from "../../components/admin/adminPrimitives";

interface WaitlistEntry {
  id: string;
  email: string;
  school_name: string | null;
  note: string | null;
  created_at: string;
}

const PAGE_SIZE = 25;

export function Waitlist() {
  const { user } = useAuthStore();
  const [allowedEmails, setAllowedEmails] = useState<Set<string>>(new Set());
  const [promoting, setPromoting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WaitlistEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from("waitlist")
      .select("id, email, school_name, note, created_at", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      const safe = q.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      query = query.or(
        `email.ilike.%${safe}%,school_name.ilike.%${safe}%,note.ilike.%${safe}%`,
      );
    }

    const { data, count, error: fetchError } = await query;
    if (fetchError) {
      console.error("Waitlist fetch error:", fetchError);
      setError("Failed to load waitlist: " + fetchError.message);
      setEntries([]);
      setTotal(0);
    } else {
      const emails = (data ?? []).map((entry) => entry.email);
      const { data: allowed, error: allowlistError } = emails.length
        ? await supabase.from("signup_allowlist").select("email").in("email", emails)
        : { data: [], error: null };
      setAllowedEmails(new Set((allowed ?? []).map((entry) => entry.email)));
      if (allowlistError) setError("Could not load allowlist status. Existing entries will not be overwritten.");
      setEntries((data ?? []) as WaitlistEntry[]);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, debouncedSearch]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  async function promote(entry: WaitlistEntry) {
    if (promoting) return;
    setPromoting(entry.id);
    setError(null);
    setNotice(null);
    try {
      const { error: insertError } = await supabase.from("signup_allowlist").insert({
        email: entry.email,
        note: [entry.school_name ? `School: ${entry.school_name}` : null, entry.note].filter(Boolean).join("\n") || null,
        added_by: user?.id ?? null,
      });
      if (insertError && insertError.code !== "23505") {
        setError("Could not add to allowlist: " + insertError.message);
        return;
      }
      setAllowedEmails((previous) => new Set([...previous, entry.email]));
      setNotice(`${entry.email} ${insertError ? "is already on" : "was added to"} the allowlist. Open the allowlist to send an invitation.`);
    } catch {
      setError("Could not confirm the allowlist update. Refresh the list before trying again.");
    } finally {
      setPromoting(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    const { error: delError } = await supabase
      .from("waitlist")
      .delete()
      .eq("id", pendingDelete.id);
    setDeleting(false);
    if (delError) {
      setError("Failed to remove entry: " + delError.message);
      setPendingDelete(null);
      return;
    }
    setPendingDelete(null);
    await fetchEntries();
  }

  const columns: AdminTableColumn<WaitlistEntry>[] = [
    {
      key: "email",
      header: "Email",
      render: (entry) => (
        <span className="font-mono text-xs text-forest-900">{entry.email}</span>
      ),
    },
    {
      key: "school",
      header: "School",
      render: (entry) => (
        <span className="text-forest-600">{entry.school_name ?? "\u2014"}</span>
      ),
    },
    {
      key: "joined",
      header: "Joined",
      render: (entry) => (
        <span className="text-forest-500">
          {new Date(entry.created_at).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (entry) => (
        <div className="flex items-center justify-end gap-3">
        {allowedEmails.has(entry.email) ? (
          <Link to="/admin/allowlist" className="text-xs font-medium text-forest-700 underline underline-offset-2">
            On allowlist
          </Link>
        ) : (
          <button
            onClick={() => promote(entry)}
            disabled={promoting !== null || deleting}
            aria-label={`Add ${entry.email} to allowlist`}
            className="rounded-lg bg-forest-900 px-3 py-1.5 text-xs font-medium text-cream-100 hover:bg-forest-800 disabled:opacity-50"
          >
            {promoting === entry.id ? "Adding..." : "Add to allowlist"}
          </button>
        )}
        <button
          onClick={() => setPendingDelete(entry)}
          disabled={promoting !== null || deleting}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50"
          aria-label={`Remove ${entry.email}`}
        >
          <TrashIcon className="h-4 w-4" />
          Remove
        </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Waitlist"
        description={
          <>
            Add people to the allowlist here, then send their invitation from the{" "}
            <Link
              to="/admin/allowlist"
              className="font-medium text-forest-700 underline decoration-forest-300 underline-offset-2"
            >
              signup allowlist
            </Link>
            .
          </>
        }
        actions={
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search email, school, or note"
          />
        }
      />

      {error && <p role="alert" className="mb-3 text-sm text-red-600">{error}</p>}
      {notice && <p role="status" className="mb-3 text-sm text-forest-700">{notice}</p>}

      <AdminTable
        columns={columns}
        rows={entries}
        loading={loading}
        emptyMessage={
          debouncedSearch
            ? `No waitlist entries match "${debouncedSearch}".`
            : "No one on the waitlist yet."
        }
      />

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmModal
        open={pendingDelete !== null}
        title="Remove from waitlist?"
        description={
          pendingDelete && (
            <>
              <span className="font-mono text-forest-900">
                {pendingDelete.email}
              </span>{" "}
              will be removed from the waitlist. They can re-join from the
              public landing page at any time.
            </>
          )
        }
        confirmLabel="Remove"
        confirmTone="red"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleting}
      />
    </div>
  );
}
